import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Checkbox,
  InlineNotification,
  Modal,
  ModalBody,
  Search,
  Tag,
} from '@carbon/react';
import { useWellboreExclusions } from '../hooks';
import type { Well, Wells, WellboreExcludedWell } from '../types';
import styles from './manage-slots-modal.module.scss';

// Combined row shape used by the table inside the modal. We merge the
// intersecting and excluded_by_wellbore wells from the filtered_results so
// the user sees every current-development slot in one place — the staged
// checkbox indicates whether it WILL be excluded after Save.
interface SlotRow {
  uwi: string;
  unitName: string;
  leaseName: string;
  status: string;
  currentOperator: string;
  // True when this slot is currently in the excluded_by_wellbore bucket.
  // Distinct from staged: the checkbox can be on/off independent of this.
  isCurrentlyExcluded: boolean;
  exclusionReason: string | null;
}

interface ManageSlotsModalProps {
  isOpen: boolean;
  onClose: () => void;
  // The wells object from filtered_results / current_development response.
  wells: Wells | undefined;
  // Optional callback fired after a successful save so the parent can
  // refetch current_development to pick up the new bucket assignment.
  onSaved?: () => void;
}

const buildRows = (wells: Wells | undefined): SlotRow[] => {
  if (!wells) return [];

  const intersectingRows = (wells.intersecting ?? []).map<SlotRow>(
    (w: Well) => ({
      uwi: w.uwi,
      unitName: w.unitName,
      leaseName: w.leaseName,
      status: w.status,
      currentOperator: w.currentOperator,
      isCurrentlyExcluded: false,
      exclusionReason: null,
    })
  );

  const excludedRows = (wells.excludedByWellbore ?? []).map<SlotRow>(
    (w: WellboreExcludedWell) => ({
      uwi: w.uwi,
      unitName: w.unitName,
      leaseName: w.leaseName,
      status: w.status,
      currentOperator: w.currentOperator,
      isCurrentlyExcluded: true,
      exclusionReason: w.wellboreExclusionReason,
    })
  );

  return [...intersectingRows, ...excludedRows].sort((a, b) => {
    if (a.unitName !== b.unitName) return a.unitName.localeCompare(b.unitName);
    return a.uwi.localeCompare(b.uwi);
  });
};

export const ManageSlotsModal: React.FC<ManageSlotsModalProps> = ({
  isOpen,
  onClose,
  wells,
  onSaved,
}) => {
  const { stagedUwis, resetStaged, toggleStaged, save } =
    useWellboreExclusions();
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const rows = useMemo(() => buildRows(wells), [wells]);
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const needle = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        r.uwi.toLowerCase().includes(needle) ||
        r.leaseName?.toLowerCase().includes(needle) ||
        r.unitName?.toLowerCase().includes(needle) ||
        r.currentOperator?.toLowerCase().includes(needle)
    );
  }, [rows, search]);

  // When the modal opens, seed the staged set from the current committed set.
  useEffect(() => {
    if (isOpen) {
      resetStaged();
      setSearch('');
      setError(null);
    }
  }, [isOpen, resetStaged]);

  const stagedCount = stagedUwis.size;
  const hasChanges = useMemo(() => {
    const committedRowsExcluded = new Set(
      rows.filter((r) => r.isCurrentlyExcluded).map((r) => r.uwi)
    );
    if (committedRowsExcluded.size !== stagedUwis.size) return true;
    for (const uwi of stagedUwis) {
      if (!committedRowsExcluded.has(uwi)) return true;
    }
    return false;
  }, [rows, stagedUwis]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    const result = await save();
    setIsSaving(false);
    if (result.success) {
      onSaved?.();
      onClose();
    } else {
      setError(result.error);
    }
  };

  // Don't mount the modal body until we're opening — keeps the Slots column
  // tests clean (no duplicate row content) and avoids running buildRows on
  // every parent re-render.
  if (!isOpen) return null;

  // Portal to document.body so the modal escapes any parent wizard's
  // stacking context (e.g. the create-package CreateTearsheet whose
  // sticky Cancel/Back/Next footer sits at z-index 9000+).
  return createPortal(
    <Modal
      open={isOpen}
      size="lg"
      modalHeading="Manage Slots"
      modalLabel="Wellbore Exclusions"
      primaryButtonText={isSaving ? 'Saving…' : 'Save'}
      secondaryButtonText="Cancel"
      onRequestClose={onClose}
      onRequestSubmit={handleSave}
      onSecondarySubmit={onClose}
      primaryButtonDisabled={isSaving || !hasChanges}
      preventCloseOnClickOutside
      hasScrollingContent
    >
      <ModalBody>
        <p className={styles.helpText}>
          Excluded slots are kept on this subtract for visibility but do not
          contribute to NRI/WI calculations or downstream evaluations. Toggle a
          checkbox to mark a slot as excluded; changes are not committed until
          you click Save.
        </p>
        {error && (
          <InlineNotification
            kind="error"
            title="Save failed"
            subtitle={error}
            hideCloseButton
            lowContrast
          />
        )}
        <div className={styles.toolbar}>
          <Search
            id="manage-slots-search"
            labelText="Search slots"
            placeholder="Search by UWI, lease, unit, or operator"
            size="sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Tag type="gray">{stagedCount} excluded</Tag>
        </div>
        <table className={styles.table}>
          <thead>
            <tr>
              <th aria-label="Excluded" />
              <th>UWI</th>
              <th>Lease</th>
              <th>Unit</th>
              <th>Status</th>
              <th>Operator</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.empty}>
                  No slots match your search.
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <tr
                  key={`${row.unitName}::${row.uwi}`}
                  className={
                    stagedUwis.has(row.uwi) ? styles.excludedRow : undefined
                  }
                >
                  <td>
                    <Checkbox
                      id={`exclude-${row.uwi}`}
                      labelText=""
                      hideLabel
                      checked={stagedUwis.has(row.uwi)}
                      onChange={() => toggleStaged(row.uwi)}
                    />
                  </td>
                  <td>{row.uwi}</td>
                  <td>{row.leaseName ?? '—'}</td>
                  <td>{row.unitName}</td>
                  <td>{row.status}</td>
                  <td>{row.currentOperator ?? '—'}</td>
                  <td>{row.isCurrentlyExcluded ? row.exclusionReason : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </ModalBody>
    </Modal>,
    document.body
  );
};
